import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import { CommonButton } from '../../../../Styles/CommonStyles';
import UnivReqFlow from './UnivReqFlow';
import TextField from '@mui/material/TextField';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import UnivMyAssetCom from '../../Myasset/UnivMyAssetComp';
import useAuth from '../../../../features/hooks/useAuth';
import { useSelector } from 'react-redux';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import EditIcon from '@mui/icons-material/Edit';
import PrintIcon from '@mui/icons-material/Print';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { generateAssetsPDF } from '../utils/pdfGenerator';
import { useGetUsersQuery } from '../../../../features/users/usersApiSlice';
import nameStringFn from '../../../../data/nameStringFn';
import { useUpdateassetReqMutation, useUpdateBoPartialAssetMutation } from '../../../../features/assetrequest/assetReqApiSlice';
import { useGetofficesQuery } from '../../../../features/offices/officesApiSlice';
import { useGetStoreOfficesQuery } from '../../../../features/storeoffices/storeofficesApiSlice';
import { useGetAssetMastersQuery } from '../../../../features/assetmasterApiSlice/assetmasterApiSlice';
import Autocomplete from '@mui/material/Autocomplete';
import UnivReqSelector from './UnivReqSelector';

// Import role-specific form components
import UserInboxForm from './UserInboxForm';
import BoInboxForm from './BoInboxForm';
import UnitHeadInboxForm from './UnitHeadInboxForm';
import StoreL1InboxForm from './StoreL1InboxForm';
import StoreL2InboxForm from './StoreL2InboxForm';

// Styled Components
const Container = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', sans-serif;
`;

const BackButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
`;

const BackButton = styled.div`
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  color: #1976d2;

  &:hover {
    text-decoration: underline;
  }
`;

const Button = styled(CommonButton)`
  padding: 8px 16px;
  min-width: 120px;
  margin-right: 10px;
`;

const ActionButton = styled(Button)`
  background-color: #1976d2;
`;

const ReturnButton = styled(Button)`
  background-color: #ff9800;
`;

const RejectButton = styled(Button)`
  background-color: #f44336;
`;

const ForwardButton = styled(Button)`
  background-color: #4caf50;
`;

const IssueButton = styled(Button)`
  background-color: #ff9800;
`;

const FinalIssueButton = styled(Button)`
  background-color: #2e7d32;
`;

const AcceptPartialButton = styled(Button)`
  background-color: #4caf50;
  font-weight: 500;
`;

const ActionContainer = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 15px;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  align-self: flex-start;
`;

const ButtonContainer = styled.div`
  display: flex;
  margin-top: 15px;
`;

const RemarkContainer = styled.div`
  margin-top: 15px;
  width: 100%;
`;

const ModalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80%',
  maxHeight: '80vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: '8px',
  overflow: 'auto'
};

const ModalTitle = styled.h2`
  margin-bottom: 20px;
  color: #1976d2;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  gap: 10px;
`;

const SelectedAssetsContainer = styled.div`
  margin-top: 20px;
  width: 100%;
`;

const SelectedAssetsTitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const SelectedAssetsTitle = styled.h3`
  margin-bottom: 0;
  color: #1976d2;
`;

const StyledTableContainer = styled(TableContainer)`
  margin-bottom: 20px;
`;

const StyledTableHead = styled(TableHead)`
  background-color: #1976d2;
  
  & th {
    color: white;
    font-weight: bold;
  }
`;

const SummaryContainer = styled.div`
  margin-bottom: 15px;
  padding: 10px;
  background-color: #f5f5f5;
  border-radius: 5px;
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
`;

const SummaryItem = styled.div`
  display: flex;
  align-items: center;
  background-color: white;
  padding: 5px 10px;
  border-radius: 5px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
`;

const SummaryItemName = styled.span`
  font-weight: 500;
  margin-right: 10px;
`;

const SummaryItemCount = styled.span`
  background-color: #1976d2;
  color: white;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.8rem;
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 10px;
`;

// Add new styled components for the partial assets modal
const PartialAssetsModalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '500px',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: '8px'
};

const OptionButton = styled(Button)`
  margin: 8px 0;
  width: 100%;
  padding: 12px;
  justify-content: flex-start;
  text-transform: none;
  font-weight: 500;
`;

const ForwardStoreButton = styled(OptionButton)`
  background-color: #2196f3;
`;

const CloseRequestButton = styled(OptionButton)`
  background-color: #f44336;
`;

// New styled components for the modify selection modal
const ModifySelectionModalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80%',
  maxHeight: '80vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: '8px',
  overflow: 'auto'
};

const AssetNameselCon = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  width: 100%;
  margin-bottom: 15px;
`;

const SelectContainer = styled.div`
  flex: 1 1 280px;
  min-width: 260px;
`;

const FormItemsCont = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 15px;
`;

const InputFieldWrap = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const AddRowButton = styled(CommonButton)`
  margin-top: 10px;
  align-self: flex-start;
`;

const RemoveRowButton = styled(CommonButton)`
  background-color: #f86946;
`;

const AssetRequestDetails = ({ row, subTabValue, setChangepage }) => {
  const [remark, setRemark] = useState('');
  const [showActionOptions, setShowActionOptions] = useState(false);
  const [openAssetModal, setOpenAssetModal] = useState(false);
  const [filterProps, setFilterProps] = useState({});
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [userRole, setUserRole] = useState(["user"]);
  const [openPartialModal, setOpenPartialModal] = useState(false);
  const [validationResults, setValidationResults] = useState({});

  // New state for modify selection modal
  const [openModifyModal, setOpenModifyModal] = useState(false);
  const [modifiedAssets, setModifiedAssets] = useState([{ catName: "", assetName: "", quantity: "" }]);
  const [storeSelectedValue, setStoreSelectedValue] = useState('');

  // New state to track whether options are loaded
  const [optionsLoaded, setOptionsLoaded] = useState(false);

  const editor = useRef(null);
  const auth = useAuth();
  let user_current_office = (useSelector((state) => state.office.off));

  // Get store offices data
  const { storeoffices } = useGetStoreOfficesQuery('storeoffices', {
    selectFromResult: ({ data }) => ({
      storeoffices: data?.entities
    }),
  });

  // Get asset master data
  const { assetmaster } = useGetAssetMastersQuery('assetmasters', {
    selectFromResult: ({ data }) => ({
      assetmaster: data?.entities
    }),
  });

  const { getSingleuser } = useGetUsersQuery('userslist', {
    selectFromResult: ({ data }) => ({
      getSingleuser: data?.entities
    }),
  });

  const { officedata } = useGetofficesQuery('offices', {
    selectFromResult: ({ data }) => ({
      officedata: data?.entities
    }),
  });

  const BO_id = officedata ? Object.values(officedata)?.find(office => office?.name === user_current_office)?.Bo : "";
  const UnitHead_id = officedata ? Object.values(officedata)?.find(office => office?.name === user_current_office)?.unitHead : "";

  const [updateReq, { isLoading: updateLoading }] = useUpdateassetReqMutation();
  const [updateBoPartialAsset, { isLoading: updateBoPartialLoading }] = useUpdateBoPartialAssetMutation();

  // Check if user is a BO (Branch Officer)
  const isBO = auth?.roles?.main?.includes('Bo') ||
    Object.values(auth?.roles || {}).some(roles =>
      Array.isArray(roles) && roles.includes('Bo'));

  // Check if user is a Unit Head
  const isUnitHead = auth?.roles?.main?.includes('UnitHead') ||
    Object.values(auth?.roles || {}).some(roles =>
      Array.isArray(roles) && roles.includes('UnitHead'));

  // Check if this is a request sent back for modification
  const isModificationRequest = row?.req_data?.[row.req_data.length - 1]?.sent_to_tag === 'boreq_back_user_mod' &&
    row?.req_data?.[row.req_data.length - 1]?.sent_to?.id === auth?.id;

  // Check if this is a partial issue request sent for approval to Unit Head
  const isPartialIssueApproval = row?.req_data?.[row.req_data.length - 1]?.sent_to_tag === 'boreq_parissueclse_forapprv' &&
    row?.req_data?.[row.req_data.length - 1]?.sent_to?.id === auth?.id;

  let senttoData = {
    sent_to_tag: "",
    Text: "",
    sent_by: {},
    sent_to: {},
    action: "",
    curr_sent_to_string: "",
    bo_partial_assets: {},
    time_stamp: null
  }

  function validateAssets(requested, available) {
    const result = [];
    const alerts = [];
    let partialIssue = false;

    const requestMap = {};
    for (const req of requested) {
      requestMap[req.assetName] = parseInt(req.quantity, 10);
    }

    // Group available assets by name
    const availableMap = {};
    for (const asset of available) {
      const key = asset.name;
      if (!availableMap[key]) {
        availableMap[key] = [];
      }
      availableMap[key].push(asset);
    }

    // Validate requested assets
    for (const [name, quantity] of Object.entries(requestMap)) {
      const matchingAssets = availableMap[name] || [];
      const availableQty = matchingAssets.length;

      if (availableQty > quantity) {
        alerts.push(`More assets selected for "${name}" than requested (requested ${quantity}, found ${availableQty}).`);
      } else if (availableQty < quantity) {
        partialIssue = true; // Do NOT push alert
      }

      result.push({
        assetName: name,
        required: quantity,
        available: availableQty,
        matchedAssets: matchingAssets.slice(0, quantity)
      });

      delete availableMap[name];
    }

    // Check for unexpected asset types
    for (const unexpected of Object.keys(availableMap)) {
      alerts.push(`Unexpected asset found: "${unexpected}" is not in the requested list.`);
      result.push({
        assetName: unexpected,
        required: 0,
        available: availableMap[unexpected].length,
        matchedAssets: availableMap[unexpected]
      });
    }

    return { result, alerts, partialIssue };
  }

  const handleBack = () => {
    setChangepage([true, false, false, false, false]);
  };

  const handleTakeAction = () => {
    setShowActionOptions(true);
  }; 

  const handleCloseModal = () => {
    setOpenAssetModal(false);
    setSelectedOptions([]);
  };

  const handleConfirmSelection = async () => {
    if (selectedOptions?.length === 0) {
      alert("Please select at least one asset to issue.");
      return;
    }

    //If BO is issuing assets, validate the selected assets.
    if (["ureq_created"]?.includes(row?.req_data[row?.req_data?.length - 1]?.sent_to_tag)) {
      // Generate the senttoData object with the selected assets
      const validationResults = validateAssets(row?.assets_requested[0]?.data, selectedOptions);
      setValidationResults(validationResults);

      if (validationResults?.alerts?.length) {
        alert("⚠️ Alerts:\n" + validationResults.alerts.join("\n"));
        return;
      }
    }

    // Close modal and show success message
    setOpenAssetModal(false);
    alert("Assets selected for issue.");
  };
  

  // Handle partial assets with forwarding to store
  const handlePartialAndForward = async () => {
    // Logic to issue partial assets and forward request to store
    senttoData.Text = remark;
    senttoData.sent_to_tag = "requester_back_partial";
    senttoData.action = "Request send back to requester to accept partial assets before forwarding to store";
    senttoData.sent_by = { id: auth?.id, name: getSingleuser?.[auth?.id]?.name, office: user_current_office, designation: auth?.designation?.[user_current_office] };
    senttoData.sent_to = { id: row?.req_creator_id, name: getSingleuser?.[row?.req_creator_id]?.name, office: row?.req_data[0]?.sent_by?.office, designation: row?.req_data[0]?.sent_by?.designation };
    senttoData.bo_partial_assets = { time_stamp: new Date(), status: "draft", assets: selectedOptions.map(x => x.id) };
    senttoData.curr_sent_to_string = `${nameStringFn(getSingleuser?.[row?.req_creator_id])}-${row?.req_data[0]?.sent_by?.office}-${getSingleuser?.[row?.req_creator_id]?.designation?.[user_current_office] || 'Unknown'}`
    senttoData.time_stamp = new Date();
    const body = { id: row?._id, senttoData: senttoData };

    const res = await updateReq(body);
    console.log(res);
    alert("Partial assets issued for requester to confirm. Request will be availble in inbox for forwarding to store after acceptance by requester.");
    setOpenPartialModal(false);
    handleBack();
  };

  // Handle partial assets and close request
  const handlePartialAndClose = async () => {
    // Logic to issue partial assets and close the request
    senttoData.Text = remark;
    senttoData.sent_to_tag = "boreq_parissueclse_forapprv";
    senttoData.action = "Partial Assets issue along with closure sent for approval of Unit Head";
    senttoData.sent_by = { id: auth?.id, name: getSingleuser?.[auth?.id]?.name, office: user_current_office, designation: auth?.designation?.[user_current_office] };
    senttoData.sent_to = { id: UnitHead_id, name: getSingleuser?.[UnitHead_id]?.name, office: user_current_office, designation: getSingleuser?.[UnitHead_id].designation?.[user_current_office] };
    senttoData.bo_partial_assets = { time_stamp: new Date(), status: "draft", assets: selectedOptions.map(x => x.id) };
    senttoData.curr_sent_to_string = `${nameStringFn(getSingleuser?.[UnitHead_id])}-${user_current_office}-${getSingleuser?.[UnitHead_id]?.designation?.[user_current_office] || 'Unknown'}`
    senttoData.time_stamp = new Date();
    const body = { id: row?._id, senttoData: senttoData };

    const res = await updateReq(body);
    console.log(res);
    alert("Partial Assets issue along with closure sent for approval of Unit Head. Request will be closed after approval and acceptance by User.");
    setOpenPartialModal(false);
    handleBack();
  };

  // Handle closing the partial assets modal
  const handleClosePartialModal = () => {
    setOpenPartialModal(false);
  };
 

  // Track when options are loaded
  useEffect(() => {
    if (storeoffices && assetmaster) {
      setOptionsLoaded(true);
    }
  }, [storeoffices, assetmaster]);

  // Handler for closing the modify selection modal
  const handleCloseModifyModal = () => {
    // Reset modified assets to empty array
    setModifiedAssets([{ catName: "", assetName: "", quantity: "" }]);
    setOpenModifyModal(false);
  };

  // Handler for asset field changes
  const handleAssetChange = (event, index, newValue, name) => {
    let tempField = [...modifiedAssets];
    if (event?.target?.name) {
      tempField[index][event.target.name] = event.target.value;
      setModifiedAssets(tempField);
    } else {
      tempField[index][name] = newValue === "" ? null : newValue;
      setModifiedAssets(tempField);
    }
  };

  // Handler for confirming the modified asset selection
  const handleConfirmModification = async () => {
    // Validate that all fields are filled
    const allFieldsFilled = modifiedAssets.every(asset =>
      asset.catName && asset.assetName && asset.quantity
    );

    if (!allFieldsFilled) {
      alert("Please fill all asset details before confirming.");
      return;
    }

    setOpenModifyModal(false);
  };  

  useEffect(() => {
    if (isBO) {
      setFilterProps({ perm_cur_loc: ["office"], perm_alloc_ofc: [user_current_office] });
    }
  }, [isBO]);
  
  const columnFilter = ["OEM/ORG Tag", "OEM/Brand", "Purchase Date", "Warranty Date", "Detail", "Model"]

  console.log(modifiedAssets);  

  // Check user roles
  useEffect(() => {
    if (auth) {
      const newRoles = ["user"]; // Start with a base role

      const isBO = auth?.roles?.main?.includes('Bo') ||
        Object.values(auth?.roles || {}).some(rolesArray =>
          Array.isArray(rolesArray) && rolesArray.includes('Bo'));
      
      const isUnitHead = auth?.roles?.main?.includes('UnitHead') ||
        Object.values(auth?.roles || {}).some(rolesArray =>
          Array.isArray(rolesArray) && rolesArray.includes('UnitHead'));
      
      const isStoreL1 = auth?.roles?.main?.includes('L1ReqApprover_A') ||
        Object.values(auth?.roles || {}).some(rolesArray =>
          Array.isArray(rolesArray) && rolesArray.includes('L1ReqApprover_A'));
      
      const isStoreL2 = auth?.roles?.main?.includes('L2ReqApprover_A') ||
        Object.values(auth?.roles || {}).some(rolesArray =>
          Array.isArray(rolesArray) && rolesArray.includes('L2ReqApprover_A'));
      
      if (isStoreL2) newRoles.push("storeL2");
      if (isStoreL1) newRoles.push("storeL1");
      if (isUnitHead) newRoles.push("unitHead");
      if (isBO) newRoles.push("bo");
      
      // To prevent unnecessary re-renders if roles haven't actually changed
      // This is a more robust way to avoid loops if `auth` itself is stable
      setUserRole(prevRoles => {
        const finalRoles = [...new Set(newRoles)]; // Ensure unique roles
        // Check if the new roles are different from the previous ones
        if (prevRoles.length === finalRoles.length && prevRoles.every((role, index) => role === finalRoles[index])) {
          return prevRoles; // Return the old state if no change
        }
        return finalRoles; // Return the new state
      });
    }
  }, [auth]);


  // Render the appropriate form based on user role and request status
  const renderRoleSpecificForm = () => {
    // If the request is not meant for this user, don't show action options
    if (!isRequestForUser()) {
      return null;
    }

    if (userRole.includes("user") && subTabValue === 0 ) {
      return <UserInboxForm row={row} handleBack={handleBack} selectedOptions={selectedOptions} />;
    }
    if (userRole.includes("bo") && subTabValue === 1) {
      return <BoInboxForm row={row} handleBack={handleBack} selectedOptions={selectedOptions} setSelectedOptions={setSelectedOptions} />;
    }
    if (userRole.includes("unitHead") && subTabValue === 2) {
      return <UnitHeadInboxForm row={row} handleBack={handleBack} />;
    }
    if (userRole.includes("storeL1") && subTabValue === 3) {
      return <StoreL1InboxForm row={row} handleBack={handleBack} />;
    }
    if (userRole.includes("storeL2") && subTabValue === 4) {
      return <StoreL2InboxForm row={row} handleBack={handleBack} />;
    }
    return null;
  };
console.log(userRole)
console.log(subTabValue)
  // Helper function to check if this request is for the current user to action
  const isRequestForUser = () => {
    const lastAction = row?.req_data?.[row.req_data.length - 1];
    if (!lastAction) return false;
    
    return lastAction.sent_to?.id === auth?.id;
  };

  // Check if the request was closed or rejected
  const isRequestClosedOrRejected = 
    row?.curr_status === 'Closed' || 
    row?.curr_status === 'Rejected' ||
    row?.req_data?.[row.req_data.length - 1]?.sent_to_tag === 'boreq_rejct_closed' ||
    row?.req_data?.[row.req_data.length - 1]?.sent_to_tag === 'ureq_paraccept_close' ||
    row?.req_data?.[row.req_data.length - 1]?.sent_to_tag === 'ureq_allasstbolvl_close' ||
    row?.req_data?.[row.req_data.length - 1]?.sent_to_tag === 'lvl2req_rejection_appvd';

  return (
    <Container>
      <BackButtonWrapper>
        <BackButton onClick={handleBack}>
          <ArrowLeftIcon />
          <span style={{ marginLeft: '8px' }}>Back to Inbox</span>
        </BackButton>
      </BackButtonWrapper>

      <UnivReqFlow row={row} />

      <ActionContainer>
        {!showActionOptions ? (
          <ButtonsWrapper>
            {/* Don't show Take Action button if the request was rejected or closed,
                or if the request is not meant for this user */}
            {!isRequestClosedOrRejected && isRequestForUser() && (
              <Button onClick={handleTakeAction}>Take Action</Button>
            )}
          </ButtonsWrapper>
        ) : (
          <>
            {renderRoleSpecificForm()}
          </>
        )}
      </ActionContainer>

      {/* Modal for Asset Selection */}
      <Modal
        open={openAssetModal}
        onClose={handleCloseModal}
        aria-labelledby="asset-selection-modal"
      >
        <Box sx={ModalStyle}>
          <ModalTitle>Select Assets to Issue</ModalTitle>

          {/* Using the universal asset component for selection */}
          <UnivMyAssetCom
            filterProps={filterProps}
            checkbox={true}
            columnFilter={columnFilter}
            selectedOptions={selectedOptions}
            setSelectedOptions={setSelectedOptions}
          />

          <ModalFooter>
            <Button onClick={handleCloseModal}>Cancel</Button>
            <ActionButton onClick={handleConfirmSelection}>Confirm Selection</ActionButton>
          </ModalFooter>
        </Box>
      </Modal>

      {/* Modal for Partial Assets Options */}
      <Modal
        open={openPartialModal}
        onClose={handleClosePartialModal}
        aria-labelledby="partial-assets-modal"
      >
        <Box sx={PartialAssetsModalStyle}>
          <ModalTitle>Partial Assets Issue Options</ModalTitle>

          <div style={{ marginBottom: '20px' }}>
            You are about to issue fewer assets than requested. Please select an option:
          </div>

          <ForwardStoreButton onClick={handlePartialAndForward}>
            Sent to unit Head for Issuing Partial Assets and Forwarding Request to Store
          </ForwardStoreButton>

          <CloseRequestButton onClick={handlePartialAndClose}>
            Sent to unit Head for Issuing Partial Assets and Close Request
          </CloseRequestButton>

          <ModalFooter>
            <Button onClick={handleClosePartialModal}>Cancel</Button>
          </ModalFooter>
        </Box>
      </Modal>

      {/* Modify Asset Selection Modal */}
      <Modal
        open={openModifyModal}
        onClose={handleCloseModifyModal}
        aria-labelledby="modify-asset-selection-modal"
      >
        <Box sx={ModifySelectionModalStyle}>
          <ModalTitle>Modify Asset Selection</ModalTitle>

          <UnivReqSelector
            field={modifiedAssets}
            setField={setModifiedAssets}
            handleChange={handleAssetChange}
            storeSelectedValue={storeSelectedValue}
            setStoreSelectedValue={setStoreSelectedValue}
            storeOffice={row?.req_store}
          />

          <ModalFooter>
            <Button onClick={handleCloseModifyModal}>Cancel</Button>
            <ActionButton onClick={handleConfirmModification}
              disabled={!storeSelectedValue || modifiedAssets.some(asset =>
                !asset.catName || !asset.assetName || !asset.quantity
              )}>
              Confirm Modification
            </ActionButton>
          </ModalFooter>
        </Box>
      </Modal>

    </Container>
  );
};

export default AssetRequestDetails;